/**
 * Hook: useChatActions
 *
 * Owns the chat-action local state and the business-logic calls for the
 * ChatActionsSheet, keeping `ChatActionsSheetContent` a UI-orchestration-only
 * component (mirrors `useUserActions` / `UserActionsContent`).
 *
 * Responsibilities:
 * - Resolves the three locally-cached predicates on mount (muted /
 *   conversation-blocked / user-blocked) via their use cases — Flutter
 *   `onBuildCompleted` (chat_actions_bottom_sheet.dart:67-75).
 * - Exposes `setBlockConversation(block)` which wraps
 *   `setBlockConversationUseCase.execute(peerId, block)` and, on success, flips
 *   the local `conversationBlocked` state (mirrors Flutter's next-open Hive
 *   re-read and `useUserActions.blockUser`).
 * - Exposes `setMute(mute)` which wraps `setMuteConversationUseCase.execute(peerId, mute)`
 *   and, on success, flips the local `muted` state (same next-open re-read
 *   parity — Flutter `_onMuteConversation`, chat_actions_bottom_sheet.dart:308-358).
 *
 * Predicate-key note (see story `## Context`): the web uses the *separated*
 * secure-storage keys — conversation-blocked ← `BLOCKED_CONVERSATIONS`,
 * user-blocked ← `BLOCKED_USERS`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { IsConversationMutedUseCase } from '@/domain/usecases/chat/isConversationMutedUseCase';
import { IsConversationBlockedUseCase } from '@/domain/usecases/chat/isConversationBlockedUseCase';
import { setBlockConversationUseCase } from '@/domain/usecases/chat/setBlockConversationUseCase';
import { setMuteConversationUseCase } from '@/domain/usecases/chat/setMuteConversationUseCase';
import { CheckUserIsBlockedUseCase } from '@/domain/usecases/user/checkUserIsBlockedUseCase';
import { blockUserUseCase } from '@/domain/usecases/user/blockUserUseCase';

export interface UseChatActionsProps {
  peerId: string;
}

export interface UseChatActionsResult {
  muted: boolean;
  conversationBlocked: boolean;
  userBlocked: boolean;
  setBlockConversation: (block: boolean) => Promise<boolean>;
  blockUser: (block: boolean) => Promise<boolean>;
  setMute: (mute: boolean) => Promise<boolean>;
}

export function useChatActions({ peerId }: UseChatActionsProps): UseChatActionsResult {
  // Default every predicate to `false` so the first paint shows the
  // un-actioned labels — exactly as Flutter does before `onBuildCompleted`
  // resolves the three reads.
  const [muted, setMuted] = useState(false);
  const [conversationBlocked, setConversationBlocked] = useState(false);
  const [userBlocked, setUserBlocked] = useState(false);

  // Use cases instantiated once.
  const isMutedUseCase = useMemo(() => new IsConversationMutedUseCase(), []);
  const isConversationBlockedUseCase = useMemo(
    () => new IsConversationBlockedUseCase(),
    [],
  );
  const checkUserIsBlockedUseCase = useMemo(
    () => new CheckUserIsBlockedUseCase(),
    [],
  );

  // Resolve the three predicates on mount (Flutter `onBuildCompleted`). Guard
  // against unmount before committing state; on any read failure log via
  // appLogger and fall back to the `false` default.
  useEffect(() => {
    const mountedRef = { current: true };
    void (async (): Promise<void> => {
      try {
        const [nextMuted, nextConversationBlocked, nextUserBlocked] =
          await Promise.all([
            isMutedUseCase.execute(peerId),
            isConversationBlockedUseCase.execute(peerId),
            checkUserIsBlockedUseCase.execute(peerId),
          ]);
        if (!mountedRef.current) return;
        setMuted(nextMuted);
        setConversationBlocked(nextConversationBlocked);
        setUserBlocked(nextUserBlocked);
      } catch (error) {
        appLogger.error(
          'Failed to read chat-action predicates',
          'useChatActions.useEffect',
          { context: { peerId }, error: error as Error },
        );
        // Predicates stay at their `false` defaults.
      }
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [
    peerId,
    isMutedUseCase,
    isConversationBlockedUseCase,
    checkUserIsBlockedUseCase,
  ]);

  // Block / un-block the conversation (Flutter `_onBlockConversation`,
  // 207-256). Wraps the mutation use case; on success flips the local state so
  // a re-open of the sheet shows the inverted label (Flutter's next-open Hive
  // re-read). Mirrors `useUserActions.blockUser`, which wraps
  // `blockUserUseCase.execute`.
  const setBlockConversation = useCallback(
    async (block: boolean): Promise<boolean> => {
      const success = await setBlockConversationUseCase.execute(peerId, block);
      if (success) {
        setConversationBlocked(block);
      }
      return success;
    },
    [peerId],
  );

  // Block / un-block the peer user (Flutter `_onBlockUser`,
  // chat_actions_bottom_sheet.dart:258-306). Wraps the toggle use case; on
  // success flips the local `userBlocked` state so a re-open of the sheet shows
  // the inverted label (Flutter's next-open Hive re-read). The use case is a
  // toggle that derives the direction from storage, so `block` is not forwarded
  // — it is used only to flip the cached state. Mirrors `setBlockConversation`
  // above and `useUserActions.blockUser`, which call
  // `blockUserUseCase.execute(userId)` with no block arg.
  const blockUser = useCallback(
    async (block: boolean): Promise<boolean> => {
      const success = await blockUserUseCase.execute(peerId);
      if (success) {
        setUserBlocked(block);
      }
      return success;
    },
    [peerId],
  );

  // Mute / un-mute the conversation (Flutter `_onMuteConversation`, 308-358).
  // Wraps the mutation use case; on success flips the local `muted` state so a
  // re-open of the sheet shows the inverted label (Flutter's next-open Hive
  // re-read). Clones `setBlockConversation`, substituting the mute use case.
  const setMute = useCallback(
    async (mute: boolean): Promise<boolean> => {
      const success = await setMuteConversationUseCase.execute(peerId, mute);
      if (success) {
        setMuted(mute);
      }
      return success;
    },
    [peerId],
  );

  return {
    muted,
    conversationBlocked,
    userBlocked,
    setBlockConversation,
    blockUser,
    setMute,
  };
}
